/**
 * The door to today's home page, LandingPage (2026-09-25).
 *
 * Since 2026-09-24 prest0.ai/ shows the new design's home page (config/site-design.json
 * lists `home` as live), so today's page is only the fallback: what `/` shows when the
 * new page's chunk cannot be loaded, and what `/` shows again if the `home` word is ever
 * taken off the list. It used to be a static import of App.tsx, so every visitor to every
 * page of prest0.ai downloaded it before the page could paint, for a page almost nobody
 * now sees. It is its own chunk, `landing`, and this is its one door (the `landing` row of
 * scripts/check-chat-first-paint.mjs, R4).
 *
 * WHY useDeferredModule AND NOT React.lazy: App.tsx records React.lazy stalling on the
 * FIRST mount of `/` in production (2026-05-09), which is why this page was made static in
 * the first place. The hook is a plain import() resolved into state, with no Suspense and
 * nothing thrown during render; the new design's own door (PavilionHomeDoor) has served
 * `/` through the same hook since 2026-09-24. It retries a failed download three times
 * before handing a chunk that is gone for good to the stale-tab self-heal.
 *
 * WHEN IT LOADS:
 *  - `/` shows today's page (the `home` word is off the list): src/index.tsx starts the
 *    chunk before the first render, so it downloads alongside main.js instead of after
 *    the router mounts this door.
 *  - the new page's chunk failed: PavilionHomeDoor renders this door as its fallback, and
 *    the chunk is fetched then.
 *  - nowhere else: no other route renders it.
 */
import React from 'react';
import { BRAND_MARK_SRC } from '../config/brandMark.generated';
import { useDeferredModule } from '../utils/useDeferredModule';
import { withSafeAnimation } from './SafeAnimationWrapper';
import { semanticLight } from '../styles/semanticTokens.generated';

// The ONE import of the page (R4 counts it): the door's hook and the boot-time preload
// below both call it, and webpack shares one request between them.
const loadLandingPage = () => import(/* webpackChunkName: "landing" */ './LandingPage');

/**
 * Start fetching today's home page before the first render. src/index.tsx calls it on `/`
 * when the page will show it. Never throws; a failure is the door's to handle.
 */
export function preloadLandingPage(): void {
  loadLandingPage().catch(() => undefined);
}

/**
 * The LandingPage hero's "0" (the mark, BRAND_MARK_SRC, incl. its ?v=) so the hero <img> is served from this request.
 * It previously preloaded a 70 KB logo file the home route never renders, fetched and thrown
 * away. (Moved here from App.tsx with the page, 2026-09-25.)
 */
const AboveTheFold: React.FC = () => (
  <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
    <link rel="preload" as="image" href={BRAND_MARK_SRC} />
  </div>
);

const LandingPageDoor: React.FC = () => {
  const { module, error } = useDeferredModule(loadLandingPage, 'landing-page', { start: 'mount' });
  // Wrapped once per loaded module, never per render: a new component type each render
  // would remount the whole page.
  const SafeLandingPage = React.useMemo(
    () => (module ? withSafeAnimation(module.default as any) : null),
    [module],
  );

  if (SafeLandingPage) {
    return (
      <>
        <AboveTheFold />
        <SafeLandingPage />
      </>
    );
  }
  // Never a blank page on a chunk that failed: the download has been retried and a chunk
  // that is gone for good handed to the stale-tab self-heal; what reaches here is the
  // error boundary's, as for every deferred route.
  if (error) throw error;

  // While the chunk arrives: the page's own ground, so its arrival is a calm fill rather
  // than a flash of a different colour. Today's page no longer has a dark mode (D-66).
  return <div data-landing-door="" aria-busy="true" style={{ minHeight: '100vh', background: semanticLight.canvas }} />;
};

export default LandingPageDoor;
